import { CSSObject } from '@emotion/react'
import {
  inkBackgroundTokens,
  inkBorderTokens,
  inkCTATokens,
  inkElevationTokens,
  inkFocusTokens,
  inkFontTokens,
} from '@ds/tokens'
import { DocusignAppId } from './types'

const headerBarHorizontalPadding = 16
const headerHeight = 64
const phoneMenuRowHeight = 72
const focusOutlineColor = inkFocusTokens.focusColor
const menuTitleFontSize = 18
const headerStandardIconSize = 20

const headerFocusCSS: CSSObject = {
  '&:focus': {
    borderRadius: 0,
    boxShadow: 'none',
    outline: `2px solid ${focusOutlineColor}`,
    outlineOffset: '-4px',
  },
}

const headerCSS: CSSObject = {
  label: 'HEADER',
  position: 'relative',
  height: headerHeight,
  fontFamily: inkFontTokens.fontBodyM.fontFamily,
}

const headerNormalizeCSS: CSSObject = {
  boxSizing: 'border-box',

  '*, *:before, *:after': {
    boxSizing: 'inherit',
  },
}

const headerBarCSS = ({ dark }: { dark?: boolean }): CSSObject => {
  return {
    label: 'HEADER_BAR',
    position: 'absolute',
    insetInlineStart: 0,
    insetInlineEnd: 0,
    top: 0,
    display: 'flex',
    justifyContent: 'space-between',
    height: headerHeight,
    backgroundColor: dark
      ? inkBackgroundTokens.bgColorInverse
      : inkBackgroundTokens.bgColorDefault,
    borderBottom: `1px solid ${inkBorderTokens.borderColorSubtle}`,
    paddingInlineStart: headerBarHorizontalPadding,
    paddingInlineEnd: headerBarHorizontalPadding,
  }
}

const headerBarMeasuringCSS: CSSObject = {
  ...headerBarCSS,
  label: 'HEADER_BAR_HIDDEN_MEASURING_CONTAINER',
  justifyContent: 'flex-start',
  display: 'flex',
  width: 0,
  visibility: 'hidden',
  backgroundColor: 'yellow',
}

const headerBarInlineStartCSS: CSSObject = {
  label: 'HEADER_BAR_INLINE_START',
  display: 'flex',
  justifyContent: 'start',
  alignItems: 'center',
}

const headerBarMiddleCSS: CSSObject = {
  label: 'HEADER_BAR_MIDDLE',
  display: 'flex',
  justifyContent: 'center',
  margin: '0px 8px',
  alignItems: 'center',
}

const headerBarMiddleMeasuringCSS: CSSObject = {
  ...headerBarMiddleCSS,
  label: 'HEADER_BAR_MIDDLE_MEASURING',
  justifyContent: 'start',
}

const headerBarInlineEndCSS: CSSObject = {
  label: 'HEADER_BAR_INLINE_END',
  display: 'flex',
  justifyContent: 'end',
  alignItems: 'center',
}

const headerBarTabsCSS: CSSObject = {
  label: 'HEADER_BAR_TABS',
  display: 'flex',
  listStyleType: 'none',
  margin: '0px',
  padding: '0px',
}

const headerLocalNavSeparatorCSS: CSSObject = {
  label: 'HEADER_LOCAL_NAV_SEPARATOR',
  marginInlineStart: 40,
}

const appDescriptionCSS = ({ dark }: { dark?: boolean }): CSSObject => {
  return {
    label: 'HEADER_APP_DESCRIPTION',
    width: 0,
    minWidth: 0,
    '&:focus': {
      boxShadow: 'none',
      outlineColor: dark ? 'white' : focusOutlineColor,
      outlineStyle: 'solid',
      outlineWidth: '2px',
      outlineOffset: '12px',
    },
  }
}

const appLogoCSS: CSSObject = {
  svg: {
    height: '37px',
    width: '111px',
  },
}

const appSwitchLogoWrapper: CSSObject = {
  height: '24px',
  maxWidth: '114px',
  paddingTop: 2,
}

const appDescriptionCSSForApp = ({
  appId = '',
  dark,
}: {
  appId: DocusignAppId
  dark?: boolean
}) => {
  let width = 0
  const height = 24
  switch (appId) {
    case 'Rebrand2024':
      width = 120
      break
    case 'Admin':
      width = 186
      break
    case 'CLM':
      width = 166
      break
    case 'DTR':
      width = 194
      break
    case 'ESign':
      width = 236
      break
    case 'Insight':
      width = 191
      break
    default:
  }
  const minWidth = width
  const maxHeight = height

  const css: CSSObject = {
    ...appDescriptionCSS({ dark }),
    width,
    minWidth,
    height,
    marginTop: 4,
    '& img': {
      minWidth,
      maxHeight,
      ...(appId !== 'Rebrand2024' &&
        dark && {
          filter: 'brightness(0) invert(100%)',
          mixBlendMode: 'normal',
        }),
    },
  }
  return css
}

const appLogoNonProdCSS = ({ dark }: { dark?: boolean }): CSSObject => {
  return {
    color: dark
      ? inkFontTokens.fontColorInverse
      : inkFontTokens.fontColorDefault,
    label: 'HEADER_APP_LOGO_NON_PROD1',
    fontFamily: inkFontTokens.fontBodyM.fontFamily,
    fontSize: inkFontTokens.fontBodyM.fontSize,
    fontWeight: inkFontTokens.fontBodyM.fontWeight,
    lineHeight: '30px',
    height: '30px',
    marginInlineStart: 12,
    paddingInlineStart: 12,
    paddingInlineEnd: 12,
    borderInlineStart: `1px solid ${inkBorderTokens.borderColorDefault}`,
    display: 'inline-block',
    whiteSpace: 'nowrap',
  }
}

const appDescriptionNonProductEmptyCSS: CSSObject = {
  label: 'HEADER_APP_DESCRIPTION_NON_PROD',
  height: 40,
  paddingTop: 4,
  textDecoration: 'none',
  display: 'flex',
  alignItems: 'center',
  '&:focus': {
    outline: `2px solid ${focusOutlineColor}`,
    outlineOffset: '8px',
  },
}

const appDescriptionNonProductCSS: CSSObject = {
  a: {
    margin: 0,
    paddingInlineStart: 0,
    paddingTop: 4,
  },
}

const appSwitcherMenuContainerCSS: CSSObject = {
  label: 'HEADER_APP_SWITCHER',
  position: 'absolute',
  top: 0,
  insetInlineStart: 0,
  backgroundColor: inkBackgroundTokens.bgColorDefault,
  border: `1px solid ${inkBorderTokens.borderColorSubtle}`,
  boxShadow: inkElevationTokens.elevationMedium,
  overflow: 'hidden',
  zIndex: 200,
}

const appSwitcherListCSS: CSSObject = {
  label: 'HEADER_APP_SWITCHER_LIST',
  listStyle: 'none',
  padding: '8px 0px',
  color: '#191823E6',
  backgroundColor: '#1918231A',
  marginBlockStart: 0,
  marginLeft: 0,
  paddingTop: 0,
  paddingBottom: 0,
}

const verticalAlignCSS: CSSObject = {
  display: 'flex',
  alignItems: 'center',
}

const menuHeadingCSS: CSSObject = {
  label: 'HEADER_MENU_HEADING',
  position: 'relative',
  padding: 24,
  height: phoneMenuRowHeight,
}

const menuHeadingCenteredCSS: CSSObject = {
  label: 'HEADER_MENU_HEADING_CENTERED',
  alignItems: 'center',
  borderBottom: `1px solid ${inkBorderTokens.borderColorSubtle}`,
  padding: '0 16px',
  display: 'flex',
}

const appSwitchHeadingCSS: CSSObject = {
  ...menuHeadingCenteredCSS,
  label: 'APP_SWITCH_MENU_HEADING',
  borderBottom: undefined,
  height: headerHeight - 2,
}

const menuCloseButtonCSS: CSSObject = {
  ...headerFocusCSS,
  label: 'HEADER_MENU_CLOSE',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  flex: '0 0 auto',
  height: headerHeight - 1,
  marginInlineEnd: '12px',
  paddingBlock: '3px 0',
  paddingInline: '12px',
}

const menuCloseFullHeaderButtonCSS: CSSObject = {
  marginInlineStart: '-1px',
}

const menuProfileChoiceList: CSSObject = {
  label: 'HEADER_PROFILE_CHOICE_LIST',
  marginTop: 10,
  listStyle: 'none',
  paddingInlineStart: 0,
}

const phoneMenuTitleCSS: CSSObject = {
  label: 'HEADER_PHONE_MENU_TITLE',
  fontSize: menuTitleFontSize,
  fontWeight: 500,
  marginBlock: '0px',
  marginInline: '8px 0px',
}

const menuSeparatorCSS: CSSObject = {
  label: 'HEADER_MENU_SEPARATOR',
  border: 0,
  height: 1,
  background: inkBorderTokens.borderColorSubtle,
  margin: 0,
}

const phoneMenuScrollableRowsCSS: CSSObject = {
  label: 'HEADER_PHONE_SCROLL_ROWS',
  flex: '1 1 auto',
  listStyle: 'none',
  margin: 0,
  overflowY: 'auto',
  padding: 0,
}

const phoneMainMenuScrollableRowsCSS: CSSObject = {
  label: 'HEADER_PHONE_MAIN_SCROLLABLE_ROW',
  flex: '1 1 auto',
  listStyle: 'none',
  margin: 0,
  overflowY: 'auto',
  padding: 0,
}

const phoneMainMenuItemListCSS: CSSObject = {
  label: 'HEADER_PHONE_MAIN_LIST',
  borderTop: '1px solid ${inkBorderTokens.borderColorSubtle}',
  margin: 0,
  listStyle: 'none',
  paddingInlineStart: 0,
}

const customLogoCSS: CSSObject = {
  label: 'HEADER_DESKTOP_CUSTOM_LOGO',
  height: 32,
  width: 'auto',
  marginInlineEnd: 16,
}

const phoneCustomLogoCSS: CSSObject = {
  label: 'HEADER_PHONE_CUSTOM_LOGO',
  height: 24,
  width: 'auto',
}

const phoneProfileRowTextCSS: CSSObject = {
  label: 'HEADER_PHONE_PROFILE_ROW_TEXT',
  maxWidth: 160,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}

const phoneCustomContentCSS: CSSObject = {
  label: 'HEADER_PHONE_CUSTOM_CONTENT',
  overflow: 'auto',
}

const menuProfileUserCSS: CSSObject = {
  label: 'HEADER_PROFILE_USER',
  color: inkFontTokens.fontColorDefault,
  fontFamily: 'inherit',
  fontWeight: 200,
  paddingBlock: '16px 24px',
  paddingInline: '24px ',
}

const phoneProfileUserCSS: CSSObject = {
  ...menuProfileUserCSS,
  label: 'HEADER_PHONE_PROFILE_USER',
  paddingBlock: '16px 24px',
  paddingInline: '24px 12px',
}

const phoneProfileUserNameCSS: CSSObject = {
  label: 'HEADER_PROFILE_USER_NAME',
  fontSize: 16,
  fontWeight: 'bold',
  margin: 0,
  maxWidth: 200,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}

const phoneMainMenuProfileUserNameCSS: CSSObject = {
  label: 'HEADER_MAIN_MENU_PROFILE_USER_NAME',
  maxWidth: 160,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const phoneProfileUserDetailCSS: CSSObject = {
  label: 'HEADER_PROFILE_USER_DETAIL',
  fontSize: 14,
  marginTop: 6,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const phoneProfileButtonCSS: CSSObject = {
  label: 'HEADER_PROFILE_MANAGE_BUTTON',
  marginTop: 16,
}

const tabsSeparatorCSS = ({ dark }: { dark?: boolean }): CSSObject => {
  return {
    label: 'HEADER_TABS_SEPARATOR',
    borderInlineStartColor: dark
      ? inkBorderTokens.borderColorSubtleInverse
      : inkBorderTokens.borderColorSubtle,
    borderInlineStartStyle: 'solid',
    borderInlineStartWidth: '1px',
    margin: '14px 0px',
  }
}

const customLogoSeparatorCSS: CSSObject = {
  label: 'HEADER_CUSTOM_LOGO_SEPARATOR',
  borderInlineStart: `1px solid ${inkBorderTokens.borderColorSubtle}`,
  height: headerHeight,
  marginInlineEnd: 16,
}

const mainButtonCSS: CSSObject = {
  label: 'HEADER_MAIN_BUTTON',
  marginInlineEnd: 20,
}

const marketingButtonTextCSS: CSSObject = {
  label: 'HEADER_MARKETING_BUTTON_SECTION',
  color: inkCTATokens.ctaBgColorPrimaryDefault,
  fontSize: '16px',
  marginInlineEnd: '16px',
  fontWeight: 500,
}

const marketingButtonSectionCSS: CSSObject = {
  label: 'HEADER_MARKETING_BUTTON_SECTION',
  marginInlineEnd: 0,
  display: 'flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
}

export default {
  verticalAlignCSS,
  headerCSS,
  headerNormalizeCSS,
  headerBarCSS,
  headerBarHorizontalPadding,
  headerBarMeasuringCSS,
  headerBarInlineStartCSS,
  headerBarMiddleCSS,
  headerBarMiddleMeasuringCSS,
  headerBarInlineEndCSS,
  headerBarTabsCSS,
  headerLocalNavSeparatorCSS,
  appDescriptionCSS,
  appDescriptionCSSForApp,
  appDescriptionNonProductCSS,
  appDescriptionNonProductEmptyCSS,
  appLogoCSS,
  appLogoNonProdCSS,
  appSwitcherMenuContainerCSS,
  appSwitcherListCSS,
  appSwitchLogoWrapper,
  appSwitchHeadingCSS,
  menuHeadingCenteredCSS,
  menuHeadingCSS,
  menuProfileChoiceList,
  menuCloseButtonCSS,
  menuCloseFullHeaderButtonCSS,
  menuSeparatorCSS,
  phoneMenuTitleCSS,
  phoneMenuScrollableRowsCSS,
  phoneMainMenuScrollableRowsCSS,
  phoneMainMenuItemListCSS,
  phoneMainMenuProfileUserNameCSS,
  customLogoCSS,
  phoneCustomLogoCSS,
  phoneProfileRowTextCSS,
  phoneCustomContentCSS,
  menuProfileUserCSS,
  phoneProfileUserCSS,
  phoneProfileUserNameCSS,
  phoneProfileUserDetailCSS,
  phoneProfileButtonCSS,
  customLogoSeparatorCSS,
  tabsSeparatorCSS,
  mainButtonCSS,
  marketingButtonTextCSS,
  marketingButtonSectionCSS,
  headerStandardIconSize,
}
